import { Component, inject, NgZone, OnInit } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { SafeArea, SystemBarsStyle, SystemBarsType } from '@capacitor-community/safe-area';
import { DeviceType } from '@app/core/types/common/device.types';
import { BaseContainer } from '@app/shared/abstract/base.container';
import { DeepLinkService } from '@app/core/services/deep-link.service';
import { NotificationService } from '@app/core/services/notification.service';
import { AuthenticationService } from '@app/core/services/authentication.service';
import { filter, first, take } from 'rxjs';
import { Platform } from '@ionic/angular';
import { MaintenanceService } from '@app/core/services/maintenance.service';
import { AnalyticsService } from '@app/core/services/analytics.service';
import { AnalyticsEvents } from '@app/core/enums/analytics-events.enum';
import { SplashScreen } from '@capacitor/splash-screen';
import { UrlService } from '@app/core/services/url.service';
import { StorageVariables } from '@app/shared/enums/storage.enums';
import { InvitationService } from '@app/core/services/invitation.service';
import { NavigationEnd, Router } from '@angular/router';
import { SurveyService } from '@app/shared/services/survey.service';

@Component({
  selector: 'c-root',
  standalone: false,
  template: `
    <ion-app>
      <ion-router-outlet></ion-router-outlet>
      @if (spotchecksInitialized()) {
        <SpotCheck />
      }
    </ion-app>
  `
})
export class AppContainer extends BaseContainer implements OnInit {
  private readonly invitationService = inject(InvitationService);
  private readonly router = inject(Router);
  private readonly surveyService = inject(SurveyService);

  spotchecksInitialized = this.surveyService.isInitialized;

  constructor(
    private ngZone: NgZone,
    private deepLinkService: DeepLinkService,
    private notificationService: NotificationService,
    private authenticationService: AuthenticationService,
    private platform: Platform,
    private analyticsService: AnalyticsService,
    private maintenanceService: MaintenanceService,
    private urlService: UrlService,
  ) {
    super();
    this.analyticsService.logEvent(AnalyticsEvents.AppOpen);
    this.urlService.init();
    this.surveyService.initSpotChecks();
  }

  async ngOnInit() {
    await this.platform.ready();

    await this.configureStatusBarStyle();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        first()
      )
      .subscribe(() => SplashScreen.hide());

    this.deepLinkService.init();

    this.maintenanceService.initMaintenanceChecks();

    await App.addListener('appUrlOpen', ({ url }) => {
      if (!url.includes('logout')) {
        this.ngZone.run(() => this.authenticationService.handleRedirect(url));
      }
    });

    // Returning from an external browser tab (e.g. the login flow via @capacitor/browser)
    // resets the Android system bars' edge-to-edge styling, so it needs to be re-applied.
    await App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        this.configureStatusBarStyle();
      }
    });

    await this.notificationService.initialize();

    this.authenticationService.authenticated$
      .pipe(
        take(1)
      ).subscribe(
        (authenticated: boolean) => {
          const invitationId = localStorage.getItem(StorageVariables.InvitationId);
          if (authenticated && invitationId) {
            this.invitationService.prefillRegistrationData(invitationId).subscribe();
          } else if (invitationId) {
            this.invitationService.clearInvitationContext();
          }  
        }
      );
  }

  private async configureStatusBarStyle(): Promise<void> {
    if (Capacitor.getPlatform() === DeviceType.Android) {
      await SafeArea.setSystemBarsStyle({ style: SystemBarsStyle.Light, type: SystemBarsType.StatusBar });
      await SafeArea.setSystemBarsStyle({ style: SystemBarsStyle.Light, type: SystemBarsType.NavigationBar });
    }
  }
}
